import { useState } from 'react';
import Modal from 'components/Modal';
import Input from 'components/inputs/Input';
import LabelWrap from 'components/inputs/LabelWrap';
import styled from 'styled-components';
import { AXIOS_INSTANCE } from 'api/mutator/custom-instance';
import { VehicleBrandRuleKind } from 'api/model';

const KIND_TEXTS: Record<
  VehicleBrandRuleKind,
  { noun: string; allModels: string }
> = {
  [VehicleBrandRuleKind.Supercar]: {
    noun: 'superbilar-regel',
    allModels: 'Alla modeller (hela märket är superbil)',
  },
  [VehicleBrandRuleKind.LowRangeElectric]: {
    noun: 'regel för dålig räckvidd',
    allModels: 'Alla modeller (märkets alla elbilar har dålig räckvidd)',
  },
};

interface SupercarBrandRule {
  id: number;
  kind: VehicleBrandRuleKind;
  brand: string;
  modelPattern: string | null;
  allModels: boolean;
  fuelType: string | null;
  excludeFuelType: boolean;
  bodyType: string | null;
  minMileage: number | null;
  maxMileage: number | null;
  minValue: number | null;
  maxValue: number | null;
  minYear: number | null;
  maxYear: number | null;
}

interface Props {
  rule?: SupercarBrandRule;
  /** Typen en ny regel får. En befintlig regel behåller sin egen. */
  kind?: VehicleBrandRuleKind;
  onCancel: () => void;
  onSaved: () => void;
}

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 10px 0;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const Hint = styled.small`
  margin-top: 4px;
  color: #666;
  font-size: 0.85em;
`;

const SectionLabel = styled.div`
  font-weight: 600;
  font-size: 0.9em;
  margin-top: 8px;
  border-top: 1px solid #eee;
  padding-top: 12px;
`;

const SupercarBrandRuleModal = ({
  rule,
  kind = VehicleBrandRuleKind.Supercar,
  onCancel,
  onSaved,
}: Props) => {
  const isEditing = !!rule;
  const ruleKind = rule?.kind ?? kind;
  const texts = KIND_TEXTS[ruleKind];
  const [brand, setBrand] = useState(rule?.brand ?? '');
  const [modelPattern, setModelPattern] = useState(rule?.modelPattern ?? '');
  const [allModels, setAllModels] = useState(rule?.allModels ?? false);
  const [fuelType, setFuelType] = useState(rule?.fuelType ?? '');
  const [excludeFuelType, setExcludeFuelType] = useState(
    rule?.excludeFuelType ?? false
  );
  const [bodyType, setBodyType] = useState(rule?.bodyType ?? '');
  const [minMileage, setMinMileage] = useState(
    rule?.minMileage != null ? String(rule.minMileage) : ''
  );
  const [maxMileage, setMaxMileage] = useState(
    rule?.maxMileage != null ? String(rule.maxMileage) : ''
  );
  const [minValue, setMinValue] = useState(
    rule?.minValue != null ? String(rule.minValue) : ''
  );
  const [maxValue, setMaxValue] = useState(
    rule?.maxValue != null ? String(rule.maxValue) : ''
  );
  const [minYear, setMinYear] = useState(
    rule?.minYear != null ? String(rule.minYear) : ''
  );
  const [maxYear, setMaxYear] = useState(
    rule?.maxYear != null ? String(rule.maxYear) : ''
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!brand.trim()) return;
    setSaving(true);
    try {
      const payload = {
        id: rule?.id ?? 0,
        kind: ruleKind,
        brand: brand.trim(),
        modelPattern: allModels ? null : modelPattern.trim() || null,
        allModels,
        fuelType: fuelType.trim() || null,
        excludeFuelType,
        bodyType: bodyType.trim() || null,
        minMileage: minMileage ? parseInt(minMileage, 10) : null,
        maxMileage: maxMileage ? parseInt(maxMileage, 10) : null,
        minValue: minValue ? parseInt(minValue, 10) : null,
        maxValue: maxValue ? parseInt(maxValue, 10) : null,
        minYear: minYear ? parseInt(minYear, 10) : null,
        maxYear: maxYear ? parseInt(maxYear, 10) : null,
      };

      if (isEditing) {
        await AXIOS_INSTANCE.post(
          '/api/VehicleClassification/UpdateSupercarRule',
          payload
        );
      } else {
        await AXIOS_INSTANCE.post(
          '/api/VehicleClassification/CreateSupercarRule',
          payload
        );
      }
      onSaved();
    } catch (e) {
      // silently ignore
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEditing ? `Redigera ${texts.noun}` : `Ny ${texts.noun}`}
      buttons={[
        {
          label: saving ? 'Sparar...' : 'Spara',
          onClick: handleSave,
        },
        {
          label: 'Avbryt',
          onClick: onCancel,
        },
      ]}
    >
      <Form>
        <LabelWrap label="Bilmärke">
          <Input
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="t.ex. Porsche, Audi, BMW"
          />
        </LabelWrap>

        <CheckboxRow>
          <input
            type="checkbox"
            checked={allModels}
            onChange={(e) => setAllModels(e.target.checked)}
          />
          {texts.allModels}
        </CheckboxRow>

        {!allModels && (
          <LabelWrap label="Modellmönster (kommaseparerat)">
            <Input
              value={modelPattern}
              onChange={(e) => setModelPattern(e.target.value)}
              placeholder="t.ex. RS, AMG, M3, M3*, *GT"
            />
            <Hint>
              Komma separerar flera mönster. Token utan <code>*</code> =
              innehåller (case-insensitive). Token med <code>*</code> = glob.
            </Hint>
          </LabelWrap>
        )}

        <SectionLabel>Ytterligare filter (valfria)</SectionLabel>

        <Row>
          <LabelWrap label="Drivmedel">
            <Input
              value={fuelType}
              onChange={(e) => setFuelType(e.target.value)}
              placeholder="t.ex. El, Bensin, Diesel"
            />
          </LabelWrap>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={excludeFuelType}
              onChange={(e) => setExcludeFuelType(e.target.checked)}
            />
            Exkludera
          </CheckboxRow>
        </Row>

        <LabelWrap label="Karosstyp">
          <Input
            value={bodyType}
            onChange={(e) => setBodyType(e.target.value)}
            placeholder="t.ex. Sedan, SUV, Transport"
          />
        </LabelWrap>

        <Row>
          <LabelWrap label="Min miltal">
            <Input
              type="number"
              value={minMileage}
              onChange={(e) => setMinMileage(e.target.value)}
              placeholder="0"
            />
          </LabelWrap>
          <LabelWrap label="Max miltal">
            <Input
              type="number"
              value={maxMileage}
              onChange={(e) => setMaxMileage(e.target.value)}
              placeholder="Inget max"
            />
          </LabelWrap>
        </Row>

        <Row>
          <LabelWrap label="Min värdering (SEK)">
            <Input
              type="number"
              value={minValue}
              onChange={(e) => setMinValue(e.target.value)}
              placeholder="0"
            />
          </LabelWrap>
          <LabelWrap label="Max värdering (SEK)">
            <Input
              type="number"
              value={maxValue}
              onChange={(e) => setMaxValue(e.target.value)}
              placeholder="Inget max"
            />
          </LabelWrap>
        </Row>

        <Row>
          <LabelWrap label="Min årsmodell">
            <Input
              type="number"
              value={minYear}
              onChange={(e) => setMinYear(e.target.value)}
              placeholder="Inget min"
            />
          </LabelWrap>
          <LabelWrap label="Max årsmodell">
            <Input
              type="number"
              value={maxYear}
              onChange={(e) => setMaxYear(e.target.value)}
              placeholder="Inget max"
            />
          </LabelWrap>
        </Row>
      </Form>
    </Modal>
  );
};

export default SupercarBrandRuleModal;
